import type { Asset } from '@/types'

export interface ParsedWalletValue {
  lovelace: bigint
  assets: Asset[]
}

export function parseWalletValue(balanceHex: string): ParsedWalletValue {
  if (!balanceHex) {
    return { lovelace: 0n, assets: [] }
  }

  try {
    const reader = new CborReader(hexToBytes(balanceHex))
    return reader.readCardanoValue()
  } catch (error) {
    console.warn('Failed to decode CIP-30 wallet balance:', error)
    return { lovelace: 0n, assets: [] }
  }
}

function hexToBytes(hex: string): Uint8Array {
  const normalized = hex.startsWith('0x') ? hex.slice(2) : hex

  if (normalized.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(normalized)) {
    throw new Error('Invalid hex string')
  }

  const bytes = new Uint8Array(normalized.length / 2)

  for (let i = 0; i < normalized.length; i += 2) {
    bytes[i / 2] = Number.parseInt(normalized.slice(i, i + 2), 16)
  }

  return bytes
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function bytesToBigInt(bytes: Uint8Array): bigint {
  let value = 0n

  for (const byte of bytes) {
    value = (value << 8n) | BigInt(byte)
  }

  return value
}

class CborReader {
  private offset = 0

  constructor(private readonly bytes: Uint8Array) {}

  readCardanoValue(): ParsedWalletValue {
    const initialByte = this.peekByte()
    const majorType = initialByte >> 5

    if (majorType === 0 || majorType === 1 || majorType === 6) {
      const lovelace = this.readInteger()
      if (lovelace < 0n) {
        throw new Error('Cardano wallet balance cannot be negative')
      }

      return { lovelace, assets: [] }
    }

    if (majorType !== 4) {
      throw new Error(`Unsupported Cardano Value major type: ${majorType}`)
    }

    const itemCount = this.readArrayLength()
    if (itemCount !== 2) {
      throw new Error(`Unexpected Cardano Value array length: ${itemCount}`)
    }

    const lovelace = this.readInteger()
    if (lovelace < 0n) {
      throw new Error('Cardano wallet balance cannot be negative')
    }

    return {
      lovelace,
      assets: this.readMultiAsset(),
    }
  }

  private readMultiAsset(): Asset[] {
    const policyCount = this.readMapLength()
    const assets: Asset[] = []

    for (let policyIndex = 0; policyIndex < policyCount; policyIndex += 1) {
      const policyId = bytesToHex(this.readByteString())
      const assetCount = this.readMapLength()

      for (let assetIndex = 0; assetIndex < assetCount; assetIndex += 1) {
        const assetName = bytesToHex(this.readByteString())
        const quantity = this.readInteger()

        assets.push({
          policyId,
          assetName,
          quantity: quantity.toString(),
        })
      }
    }

    return assets
  }

  private readInteger(): bigint {
    const initialByte = this.readByte()
    const majorType = initialByte >> 5
    const additionalInfo = initialByte & 0x1f

    if (majorType === 0) {
      return this.readUnsignedFromAdditionalInfo(additionalInfo)
    }

    if (majorType === 1) {
      return -1n - this.readUnsignedFromAdditionalInfo(additionalInfo)
    }

    if (majorType === 6) {
      const tag = this.readUnsignedFromAdditionalInfo(additionalInfo)
      if (tag !== 2n && tag !== 3n) {
        throw new Error(`Unsupported CBOR integer tag: ${tag.toString()}`)
      }

      const magnitude = bytesToBigInt(this.readByteString())
      return tag === 2n ? magnitude : -1n - magnitude
    }

    throw new Error(`Expected integer, found major type ${majorType}`)
  }

  private readArrayLength(): number {
    return this.readLengthForMajorType(4)
  }

  private readMapLength(): number {
    return this.readLengthForMajorType(5)
  }

  private readByteString(): Uint8Array {
    const initialByte = this.readByte()
    const majorType = initialByte >> 5

    if (majorType !== 2) {
      throw new Error(`Expected byte string, found major type ${majorType}`)
    }

    const length = this.lengthFromAdditionalInfo(initialByte & 0x1f)
    this.ensureAvailable(length)

    const value = this.bytes.slice(this.offset, this.offset + length)
    this.offset += length
    return value
  }

  private readLengthForMajorType(expectedMajorType: number): number {
    const initialByte = this.readByte()
    const majorType = initialByte >> 5

    if (majorType !== expectedMajorType) {
      throw new Error(`Expected major type ${expectedMajorType}, found ${majorType}`)
    }

    return this.lengthFromAdditionalInfo(initialByte & 0x1f)
  }

  private lengthFromAdditionalInfo(additionalInfo: number): number {
    if (additionalInfo === 31) {
      throw new Error('Indefinite-length CBOR items are not supported')
    }

    const value = this.readUnsignedFromAdditionalInfo(additionalInfo)
    if (value > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new Error('CBOR length exceeds Number.MAX_SAFE_INTEGER')
    }

    return Number(value)
  }

  private readUnsignedFromAdditionalInfo(additionalInfo: number): bigint {
    if (additionalInfo < 24) {
      return BigInt(additionalInfo)
    }

    if (additionalInfo === 24) {
      return BigInt(this.readByte())
    }

    if (additionalInfo === 25) {
      this.ensureAvailable(2)
      const value =
        (this.bytes[this.offset] << 8) |
        this.bytes[this.offset + 1]
      this.offset += 2
      return BigInt(value)
    }

    if (additionalInfo === 26) {
      this.ensureAvailable(4)
      const value =
        (this.bytes[this.offset] * 0x1_00_00_00) +
        (this.bytes[this.offset + 1] << 16) +
        (this.bytes[this.offset + 2] << 8) +
        this.bytes[this.offset + 3]
      this.offset += 4
      return BigInt(value >>> 0)
    }

    if (additionalInfo === 27) {
      this.ensureAvailable(8)

      let value = 0n
      for (let index = 0; index < 8; index += 1) {
        value = (value << 8n) | BigInt(this.bytes[this.offset + index])
      }

      this.offset += 8
      return value
    }

    throw new Error(`Unsupported additional info: ${additionalInfo}`)
  }

  private peekByte(): number {
    this.ensureAvailable(1)
    return this.bytes[this.offset]
  }

  private readByte(): number {
    this.ensureAvailable(1)
    const value = this.bytes[this.offset]
    this.offset += 1
    return value
  }

  private ensureAvailable(length: number): void {
    if (this.offset + length > this.bytes.length) {
      throw new Error('Unexpected end of CBOR data')
    }
  }
}
